import React, { useEffect, useState } from "react";
import { Save, X } from "lucide-react";
import SummaryApi from "../common";
import fetchWithAuth from "../common/fetchWithAuth";

export default function EmployeeForm({
    initialData = null,
    onSuccess,
    onCancel,
}) {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        designation: "",
        joiningDate: "",
        skillSet: "",
        salary: "",
        displayOnDashboard: false,
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (initialData) {
            setForm({
                name: initialData.name || "",
                email: initialData.email || "",
                phone: initialData.phone || "",
                designation: initialData.designation || "",
                joiningDate: initialData.joiningDate
                    ? new Date(initialData.joiningDate).toISOString().slice(0, 10)
                    : "",
                skillSet: Array.isArray(initialData.skillSet)
                    ? initialData.skillSet.join(", ")
                    : initialData.skillSet || "",
                salary: initialData.salary || "",
                displayOnDashboard: !!initialData.displayOnDashboard,
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.name.trim()) {
            setError("Name required");
            return;
        }

        const payload = {
            ...form,
            skillSet: form.skillSet
                ? form.skillSet
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                : [],
        };

        try {
            setLoading(true);
            let url, method;

            if (initialData && initialData._id) {
                url = SummaryApi.updateStaff.url(initialData._id);
                method = SummaryApi.updateStaff.method;
            } else {
                url = SummaryApi.createStaff.url;
                method = SummaryApi.createStaff.method;
            }

            const res = await fetchWithAuth(url, {
                method: method,
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || "Save failed");
            onSuccess && onSuccess(data.staff);
        } catch (err) {
            console.error(err);
            setError(err.message || "Save failed");
        } finally {
            setLoading(false);
        }
    };

    const inputClass = "w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all";
    const labelClass = "block text-sm font-medium text-gray-700 mb-1.5";

    return (
        <form
            onSubmit={handleSubmit}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
            {error && <div className="col-span-2 text-rose-600 bg-rose-50 p-3 rounded-lg border border-rose-100 text-sm">{error}</div>}

            <div>
                <label className={labelClass}>
                    Full Name <span className="text-red-500">*</span>
                </label>
                <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="e.g. John Doe"
                />
            </div>

            <div>
                <label className={labelClass}>Email Address</label>
                <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="e.g. john@example.com"
                />
            </div>

            <div>
                <label className={labelClass}>Phone Number</label>
                <input
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="e.g. 9876543210"
                />
            </div>

            <div>
                <label className={labelClass}>
                    Designation
                </label>
                <input
                    name="designation"
                    value={form.designation}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="e.g. Manager, Salesman"
                />
            </div>

            <div>
                <label className={labelClass}>
                    Joining Date
                </label>
                <input
                    type="date"
                    name="joiningDate"
                    value={form.joiningDate}
                    onChange={handleChange}
                    className={inputClass}
                />
            </div>

            <div>
                <label className={labelClass}>
                    Skillset (comma separated)
                </label>
                <input
                    name="skillSet"
                    value={form.skillSet}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="e.g. Sales, Accounting, React"
                />
            </div>

            <div>
                <label className={labelClass}>
                    Salary
                </label>
                <input
                    type="number"
                    name="salary"
                    value={form.salary}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="0.00"
                />
            </div>
            {/* dont know why needed  */}
            {/* <div className="flex items-center gap-3">
                <input
                    type="checkbox"
                    id="displayOnDashboard"
                    name="displayOnDashboard"
                    checked={form.displayOnDashboard}
                    onChange={handleChange}
                    className="w-5 h-5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <label htmlFor="displayOnDashboard" className="text-sm font-medium text-gray-700 select-none cursor-pointer">Display on Dashboard</label>
            </div> */}

            <div className="col-span-1 md:col-span-2 flex gap-3 justify-end mt-4 pt-4 border-t border-gray-100">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors flex items-center gap-2"
                >
                    <X size={18} />
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-lg shadow-emerald-200 transition-all flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                    {loading ? (
                        <>Saving...</>
                    ) : (
                        <>
                            <Save size={18} />
                            {initialData ? "Update Employee" : "Create Employee"}
                        </>
                    )}
                </button>
            </div>
        </form>
    );
}